import React, { KeyboardEvent, ClipboardEvent, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import parsePhoneNumberFromString from 'libphonenumber-js'
import classNames from 'classnames'

// evertel
import { validateEmail } from '@evertel/utils'
import { Col, Dropdown, CreatableDropdown, CreatableDropdownOption, InfoBox, Input, Label, Row, Tag, Text, Textarea, Button, PeopleSearchSelect, DepartmentSearchSelect } from '@evertel/web/ui'
import { roles } from '@evertel/constants'
import { SendInvitesBody } from '@evertel/invites'
import { APIDataBlueUser, APIDataDepartment } from '@evertel/types'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { CurrentUserController } from '@evertel/blue-user'

interface InviteByEmailFormProps {
    inviteType: 'department' | 'room' | 'contact'
    showRole?: boolean
    showPublicSearch?: boolean
    departmentId: number
    onChange: (body: SendInvitesBody) => void
    className?: string
}

const InviteByEmailForm: React.FC<InviteByEmailFormProps> = observer(({
    inviteType = 'department',
    showRole,
    showPublicSearch: _showPublicSearch,
    departmentId,
    onChange,
    ...otherProps
}) => {

    const sessionService = useService(SessionService, [])

    const sessionUserId = sessionService.currentUserId
    const currentUserController = useService(CurrentUserController, [sessionUserId])

    const [inviteAsRole, setInviteAsRole] = useState((inviteType === 'department') ? 'employee' : 'guest')
    const [customMessage, setCustomMessage] = useState('')
    const [shareList, setShareList] = useState<string[]>([])
    const [error, setError] = useState('')
    const [openPublicSearch, setOpenPublicSearch] = useState(false)
    const [selectedDepartment, setSelectedDepartment] = useState<APIDataDepartment>()
    const [selectedPeople, setSelectedPeople] = useState<APIDataBlueUser[]>([])

    useEffect(() => {
        if (onChange) {
            const selectedUserIds = selectedPeople?.map(p => p.id) || []

            onChange({
                to: [...shareList, ...selectedUserIds] as string[],
                roles: [inviteAsRole],
                message: customMessage
            })
        }
    }, [shareList, inviteAsRole, customMessage, onChange, selectedPeople])

    const validator = (val: string) => {
        if (!val.length) return false

        const hasLetters = /[a-zA-Z]/.test(val)
        const hasAt = val.includes('@')
        const isOnlyPhoneChars = /^[0-9()+\-\s.]+$/.test(val)

        // If it has @ symbol, treat as email
        if (hasAt) {
            if (!validateEmail(val)) {
                return 'Invalid email address'
            } else if (val.length > 80) {
                return 'Value entered is too long'
            }
        }
        // If it has letters but no @, likely meant to be an email
        else if (hasLetters) {
            return 'Invalid email address'
        }
        // If it only has phone characters, treat as phone
        else if (isOnlyPhoneChars) {
            const phoneNumber = parsePhoneNumberFromString(val, 'US')
            if (!phoneNumber?.isValid()) {
                return 'Invalid phone number'
            }
        }
        // If it has numbers and other special characters but no letters
        else {
            return 'Invalid email address or phone number'
        }

        return true
    }

    const transform = (val: string) => {
        const isEmail = val?.includes('@')
        if (isEmail) return val

        const cleanedNumber = val.replace(/\D/g, '')
        const formattedNumber = cleanedNumber.substring(0, 3) + '-' + cleanedNumber.substring(3, 6) + '-' + cleanedNumber.substring(6, 10)

        return formattedNumber
    }

    const handleChange = (selectedOptions: CreatableDropdownOption[]) => {
        const values = selectedOptions.map((option) => option.value)
        setShareList(values)
    }

    const charactersRemaining = 1000 - (customMessage?.length || 0)
    const isExecutive = currentUserController.canExecutive
    const isGuest = currentUserController.hasGuestRoleInSelectedDepartment
    const isAdmin = currentUserController.isAdmin
    const showPublicSearch = _showPublicSearch && !isGuest

    const handleRoleOptions = () => {
        if (inviteType === 'department') {
            if (isExecutive || isAdmin) return roles.filter(r => r.value !== 'guest')

            return roles.filter(r => r.value !== 'guest' && r.value !== 'executive')
        }

        return []
    }

    return (
        <Col {...otherProps}>
            {(openPublicSearch) &&
                <Row className="mb-4">
                    <Col>
                        <Row align="between">
                            <Label
                                text="Invite people from other agencies..."
                                htmlFor="public-search"
                            />
                            <Button
                                id="public-search"
                                color="link"
                                onClick={() => {
                                    setSelectedPeople([])
                                    setOpenPublicSearch(false)
                                }}>
                                close
                            </Button>
                        </Row>
                        <DepartmentSearchSelect
                            selected={[selectedDepartment] as Record<string, any>[]}
                            onSelect={(dep) => setSelectedDepartment(dep as APIDataDepartment)}
                            placeholder="Select agency first..."
                            className="w-100 mb-2"
                        />
                        <PeopleSearchSelect
                            departmentId={selectedDepartment?.id as number}
                            searchScope="public"
                            showPosition
                            isMulti
                            forceShowDepartment
                            selected={selectedPeople as APIDataBlueUser[]}
                            isDisabled={!selectedDepartment}
                            onSelect={setSelectedPeople as (selected: APIDataBlueUser | APIDataBlueUser[]) => void}
                            className="w-100"
                        />
                    </Col>
                </Row>
            }
            <Row align="between">
                <Label
                    text="To"
                    hint="Email addresses or U.S. mobile numbers. Message and data rates may apply"
                    htmlFor={`sendTo-${inviteType}`}
                />
                {(showPublicSearch && !openPublicSearch) &&
                    <Button
                        color="link"
                        onClick={() => setOpenPublicSearch(true)}>
                        Find people in other agencies
                    </Button>
                }
            </Row>
            <Row>
                <CreatableDropdown
                    id={`sendTo-${inviteType}`}
                    value={shareList.map((value) => ({ value, label: value }))}
                    onChange={handleChange}
                    onError={(error) => setError(error)}
                    validator={validator}
                    transform={transform}
                    placeholder="Please enter an email or phone number"
                    multi={true}
                    className="w-100"
                />

            </Row>
            {(error) &&
                <InfoBox
                    color="danger"
                    className="mt-2 px-3 py-1">
                    {error}
                </InfoBox>
            }
            {(showRole) &&
                <>
                    <Label
                        text="Set a role for all invites"
                        htmlFor="role"
                        className="mt-4"
                    />
                    <Dropdown
                        id="role"
                        options={handleRoleOptions()}
                        value={inviteAsRole}
                        onChange={(s) => setInviteAsRole((s as any).value)}
                    />
                </>
            }
            <Label
                text="Custom message"
                htmlFor="customMessage"
                optional
                className="mt-4"
            />
            <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value?.substring(0, 1000))}
                id="customMessage"
            />
            <Text
                size="smaller">
                1000 characters max. You have <strong className={classNames({ 'text-danger': charactersRemaining < 100 })}>{charactersRemaining}</strong> characters remaining.
            </Text>
        </Col>
    )
})

export { InviteByEmailForm }
